import React, { useEffect } from 'react';
import { Dialog, DialogContent, Box, Typography, Button, IconButton } from '@mui/material';
import { Error, Warning, Close, Info } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const ErrorModal = ({ open, onClose, title, message, type = 'error', details = null }) => {
  useEffect(() => {
    if (open) {
      // Auto-cerrar después de 8 segundos para errores informativos
      if (type === 'warning' || type === 'info') {
        const timer = setTimeout(() => {
          onClose();
        }, 8000);
        return () => clearTimeout(timer);
      }
    }
  }, [open, onClose, type]);

  // Configuración según tipo de error
  const config = {
    error: {
      icon: Error,
      color: '#d32f2f',
      gradient: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
      bgColor: '#ffebee'
    },
    warning: {
      icon: Warning,
      color: '#ed6c02',
      gradient: 'linear-gradient(135deg, #ed6c02 0%, #ff9800 100%)',
      bgColor: '#fff3e0'
    },
    info: {
      icon: Info,
      color: '#0288d1',
      gradient: 'linear-gradient(135deg, #0288d1 0%, #03a9f4 100%)',
      bgColor: '#e1f5fe'
    }
  };

  const currentConfig = config[type] || config.error;
  const IconComponent = currentConfig.icon;

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            component: motion.div,
            initial: { scale: 0.8, opacity: 0, rotateX: -15 },
            animate: { scale: 1, opacity: 1, rotateX: 0 },
            exit: { scale: 0.8, opacity: 0, rotateX: 15 },
            transition: { duration: 0.3, ease: 'easeOut' },
            style: {
              borderRadius: '16px',
              overflow: 'visible',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }
          }}
        >
          <DialogContent sx={{ p: 0, position: 'relative', overflow: 'visible' }}>
            {/* Botón de cerrar */}
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                zIndex: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <Close />
            </IconButton>

            {/* Header con gradiente */}
            <Box
              sx={{
                background: currentConfig.gradient,
                padding: '32px 24px 24px',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Icono principal con animación */}
              <Box
                component={motion.div}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
                sx={{
                  width: 80,
                  height: 80,
                  margin: '0 auto 16px',
                  borderRadius: '50%',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                  position: 'relative'
                }}
              >
                {/* Anillo pulsante */}
                <Box
                  component={motion.div}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 0, 0.5]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: `3px solid ${currentConfig.color}`,
                  }}
                />
                <IconComponent sx={{ fontSize: 48, color: currentConfig.color }} />
              </Box>

              {/* Título */}
              <Typography
                variant="h5"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  textAlign: 'center',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                {title}
              </Typography>
            </Box>

            {/* Contenido */}
            <Box sx={{ padding: '24px' }}>
              {/* Mensaje principal */}
              <Typography
                variant="body1"
                sx={{
                  color: '#424242',
                  fontSize: '16px',
                  lineHeight: 1.6,
                  textAlign: 'center',
                  mb: details ? 2 : 3
                }}
              >
                {message}
              </Typography>

              {/* Detalles adicionales */}
              {details && (
                <Box
                  sx={{
                    backgroundColor: currentConfig.bgColor,
                    borderRadius: '8px',
                    padding: '16px',
                    mb: 3,
                    border: `1px solid ${currentConfig.color}40`
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#616161',
                      fontSize: '14px',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-line'
                    }}
                  >
                    {details}
                  </Typography>
                </Box>
              )}

              {/* Botón de cerrar */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  onClick={onClose}
                  variant="contained"
                  sx={{
                    background: currentConfig.gradient,
                    color: 'white',
                    fontWeight: 600,
                    padding: '12px 40px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '16px',
                    boxShadow: `0 4px 12px ${currentConfig.color}40`,
                    '&:hover': {
                      boxShadow: `0 6px 16px ${currentConfig.color}60`,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Entendido
                </Button>
              </Box>

              {/* Barra de auto-cierre para warnings e info */}
              {(type === 'warning' || type === 'info') && (
                <Box
                  component={motion.div}
                  initial={{ scaleX: 1 }}
                  animate={{ scaleX: 0 }}
                  transition={{ duration: 8, ease: 'linear' }}
                  sx={{
                    height: 3,
                    background: currentConfig.gradient,
                    mt: 2,
                    borderRadius: 2,
                    transformOrigin: 'left'
                  }}
                />
              )}
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default ErrorModal;
